// Wire types for agent handoff (CYBORG-1231), hand-mirrored from the server's
// Cyborg*Handoff*Schema in packages/server/src/server/cyborg/cyborg-messages.ts.

export interface HandoffAgent {
  cyboId: string;
  name: string;
  avatar: string | null;
  jobTitle: string | null;
  homeDaemonId: string | null;
  homeMachineName: string | null;
  homeOnline: boolean;
  scheduleCount: number;
  /** Homed on a Worker machine that outlives the member: only ownership changes hands. */
  staysOnWorker: boolean;
  /** false: the server refuses the "Workspace machine" recipient for this agent (its Worker has no current-member owner). Absent = allowed. */
  workspaceMachineAllowed?: boolean;
}

/** The pseudo-recipient holding the workspace's Worker machines (server: WORKSPACE_RECIPIENT_ID in
 *  cyborg-messages.ts; a server test pins this copy). */
export const WORKSPACE_RECIPIENT_ID = "workspace";

/** Whether the server will accept `userId` as this agent's new owner; only a Worker-homed agent can be refused. */
export function agentAccepts(a: HandoffAgent, userId: string): boolean {
  return !(
    a.staysOnWorker &&
    userId === WORKSPACE_RECIPIENT_ID &&
    a.workspaceMachineAllowed === false
  );
}

export interface HandoffMachine {
  daemonId: string;
  name: string;
  online: boolean;
  isWorkspaceMachine: boolean;
}

export interface HandoffRecipient {
  userId: string;
  name: string;
  avatar: string | null;
  machines: HandoffMachine[];
}

export interface HandoffTarget {
  userId: string;
  daemonId: string;
}

export interface HandoffCandidates {
  /** The member's own agents (the workspace CEO agent included): the only ones with a decision. */
  agents: HandoffAgent[];
  /** Live sessions on the member's machines that close with the hand-off (other people's agents, and own agents on non-home machines). */
  hostedSessionCount: number;
  recipients: HandoffRecipient[];
  defaultTarget: HandoffTarget | null;
}

export interface AccountHandoffWorkspace extends HandoffCandidates {
  workspaceId: string;
  name: string;
  soleMember: boolean;
}

export interface AccountHandoffCandidates {
  workspaces: AccountHandoffWorkspace[];
}

export type Handoff =
  | { cyboId: string; action: "move"; toUserId: string; toDaemonId: string }
  | { cyboId: string; action: "reassign"; toUserId: string }
  | { cyboId: string; action: "delete" };

export interface WorkspaceHandoffs {
  workspaceId: string;
  handoffs: Handoff[];
}

/** The default decision per agent: given to `target`, or deleted when there is none. */
export function defaultHandoffs(agents: HandoffAgent[], target: HandoffTarget | null): Handoff[] {
  return agents.map((a): Handoff => {
    if (!target || !agentAccepts(a, target.userId)) return { cyboId: a.cyboId, action: "delete" };
    if (a.staysOnWorker) return { cyboId: a.cyboId, action: "reassign", toUserId: target.userId };
    return {
      cyboId: a.cyboId,
      action: "move",
      toUserId: target.userId,
      toDaemonId: target.daemonId,
    };
  });
}

export function countHandoffs(
  agents: HandoffAgent[],
  handoffs: Handoff[],
): { moving: number; deleting: number } {
  const handed = new Set(
    handoffs
      .filter((h) => h.action !== "delete" && agents.some((a) => a.cyboId === h.cyboId))
      .map((h) => h.cyboId),
  );
  return { moving: handed.size, deleting: agents.length - handed.size };
}

/** "3 sessions running on Ann's machines will be closed", or "" when none. */
export function hostedSessionsNote(count: number, machinesOf: string): string {
  if (count === 0) return "";
  return `${count} ${count === 1 ? "session" : "sessions"} running on ${machinesOf} will be closed`;
}

/** "Couldn't move Sprite and Atlas." — the failed agents by name (client-side, from the candidates list). */
export function failedMoveMessage(failedCyboIds: string[], agents: HandoffAgent[]): string {
  const names = failedCyboIds.flatMap((id) => agents.find((a) => a.cyboId === id)?.name ?? []);
  if (names.length !== failedCyboIds.length || names.length === 0) {
    const n = failedCyboIds.length;
    return `Couldn't move ${n} ${n === 1 ? "agent" : "agents"}.`;
  }
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Couldn't move ${list}.`;
}
